import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../../components/Topbar/Topbar";
import { todayStr, startOfMonth, RANGE_PRESETS, activePresetKey } from "../../utils/dateRange";
import { loadGmHudConfig } from "../../utils/config";
import { logActivity } from "../../utils/activity";
import "./ScoreboardPage.css";

const GAMES_CLOUD_BASE = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
    .replace(/^ws/, "http")
    .replace(/ws$/, "");

const TOP_N = 10;
const MEDALS = ["🥇", "🥈", "🥉"];

// ScoreboardPage is the GM-facing scoreboard: the top 10 combined point totals plus team
// scores, over "denne uge / denne måned / altid" or a custom range. Who may see it is a
// per-user tick in the staff sheet — this gate only stops the page rendering; the same flag is
// enforced on GET /scoreboard, which is what makes it real.
export default function ScoreboardPage() {
    const navigate = useNavigate();
    const [unauthorized, setUnauthorized] = useState(false);
    const [ready, setReady] = useState(false);
    const [from, setFrom] = useState(startOfMonth());
    const [to, setTo] = useState(todayStr());
    const [scores, setScores] = useState([]);
    const [teamScores, setTeamScores] = useState([]);
    const [totalGms, setTotalGms] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const email = localStorage.getItem("userEmail") || "";
        loadGmHudConfig().then(cfg => {
            if (!cfg?.users?.[email.toLowerCase()]?.scoreboard) { setUnauthorized(true); return; }
            setReady(true);
            logActivity("open_scoreboard");
        });
    }, []);

    function applyPreset(p) {
        setFrom(p.from());
        setTo(todayStr());
    }
    const activePreset = activePresetKey(from, to);

    const load = useCallback(async () => {
        if (!ready) return;
        setLoading(true);
        try {
            const token = localStorage.getItem("accessToken");
            const r = await fetch(`${GAMES_CLOUD_BASE}scoreboard?from=${from}&to=${to}&limit=${TOP_N}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (r.ok) {
                const data = await r.json();
                setScores(data.scores || []);
                setTeamScores(data.team_scores || []);
                setTotalGms(data.total_gms || 0);
            }
        } finally {
            setLoading(false);
        }
    }, [ready, from, to]);

    useEffect(() => { load(); }, [load]);

    if (unauthorized) return (
        <div style={{ padding: 40, color: "#e74c3c" }}>
            Ingen adgang. <button onClick={() => navigate("/")} style={{ marginLeft: 12, cursor: "pointer" }}>Tilbage</button>
        </div>
    );

    return (
        <>
            <Topbar
            />

            <div className="sb-content">
                <div className="sb-filters">
                    <div className="sb-presets">
                        {RANGE_PRESETS.map(p => (
                            <button key={p.key}
                                className={`sb-preset ${activePreset === p.key ? "active" : ""}`}
                                onClick={() => applyPreset(p)}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="sb-custom">
                        <label>Fra<input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
                        <label>Til<input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
                    </div>
                </div>

                <section className="sb-card">
                    <div className="sb-card-head">
                        <h2>Top {TOP_N}</h2>
                        {totalGms > TOP_N && <span className="sb-dim">{totalGms} GM'er i alt</span>}
                    </div>
                    {scores.length === 0 ? (
                        <div className="sb-empty">{loading ? "Henter…" : "Ingen point i perioden endnu."}</div>
                    ) : (
                        <ol className="sb-list">
                            {scores.map((s, i) => (
                                <li key={s.name} className={`sb-row ${i < 3 ? `sb-top-${i + 1}` : ""}`}>
                                    <span className="sb-rank">{MEDALS[i] || i + 1}</span>
                                    <span className="sb-name">{s.name}</span>
                                    <span className="sb-points">{s.points}</span>
                                </li>
                            ))}
                        </ol>
                    )}
                </section>

                {teamScores.length > 0 && (
                    <section className="sb-card">
                        <div className="sb-card-head"><h2>Teams</h2></div>
                        <ol className="sb-list">
                            {teamScores.map((t, i) => (
                                <li key={t.id} className={`sb-row ${i < 3 ? `sb-top-${i + 1}` : ""}`}>
                                    <span className="sb-rank">{MEDALS[i] || i + 1}</span>
                                    <span className="sb-name">
                                        {t.name}
                                        <span className="sb-members">{t.members.join(" · ")}</span>
                                    </span>
                                    <span className="sb-points">{t.points}</span>
                                </li>
                            ))}
                        </ol>
                    </section>
                )}
            </div>
        </>
    );
}
